// src/pages/Admin_Menu_Edit.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import {
  ArrowLeft,
  UserCog,
  Building2,
  Mail,
  Phone,
  MapPin,
  ShieldCheck,
  Save,
  Image as ImageIcon,
  Users as UsersIcon,
  Boxes,
} from "lucide-react";

// At the top of Admin_Menu_Edit.jsx (replace the hardcoded API too)
const API = "https://superadmin.nissiindia.com";
const MEDIA_BASE = API; // same host as API unless you override

function toAbsMediaUrl(p) {
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  if (p.startsWith("/")) return `${MEDIA_BASE}${p}`; // e.g. /media/tenants/...
  return `${MEDIA_BASE}/${p}`;
}

// --- helpers ---
function useTenantId() {
  const location = useLocation();
  const q = new URLSearchParams(location.search);
  const fromQuery = q.get("tid");
  const fromLS = useMemo(() => {
    try {
      const raw = localStorage.getItem("nissi_admin");
      if (!raw) return null;
      const obj = JSON.parse(raw);
      return obj?.tenantId || obj?.TenantId || null;
    } catch {
      return null;
    }
  }, []);
  return fromQuery || fromLS || 1;
}

export default function Admin_Menu_Edit() {
  const { username: routeUsername } = useParams();
  const tenantId = useTenantId();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [username] = useState(decodeURIComponent(routeUsername || ""));

  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    num_users: 5,
    num_licenses: 0, // keep if you still use it anywhere
    num_clients: 0,  // NEW: visit clients limit
    user_map: "google", // NEW: google | mapbox | none
    is_active: 1,
    logo_path: "",
  });

  // logo upload local state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");

  // load current data
  useEffect(() => {
    (async () => {
      if (!tenantId || !username) return;
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/api/mysql/tenant/${tenantId}/admins/${encodeURIComponent(
            username
          )}`
        );
        const row = r.data?.row;
        if (!row) throw new Error("Record not found");

        setForm({
          company_name: row.company_name || "",
          contact_name: row.contact_name || "",
          email: row.email || "",
          phone: row.phone || "",
          address: row.address || "",
          num_users: row.num_users ?? 5,
          num_licenses: row.num_licenses ?? 0,
          num_clients: row.num_clients ?? 0, // NEW
          user_map: row.user_map || "google", // NEW
          is_active: row.is_active ? 1 : 0,
          logo_path: row.logo_path || "",
        });
        setLogoPreview(toAbsMediaUrl(row.logo_path || ""));
      } catch (e) {
        console.error(e);
        Swal.fire("Error", e?.response?.data?.error || e.message, "error");
      } finally {
        setLoading(false);
      }
    })();
  }, [tenantId, username]);

  // validations
  const isEmail = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const isPhone = (v) => !v || String(v).length <= 50;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onChangeUsers = (e) => {
    const n = Math.max(1, Number(e.target.value || 1));
    setForm((f) => ({ ...f, num_users: n }));
  };

  const onChangeLicenses = (e) => {
    setForm((f) => ({ ...f, num_licenses: Number(e.target.value || 0) }));
  };

  const onChangeClients = (e) => {
    const n = Math.max(0, Number(e.target.value || 0));
    setForm((f) => ({ ...f, num_clients: n }));
  };

  const onToggleActive = () => {
    setForm((f) => ({ ...f, is_active: f.is_active ? 0 : 1 }));
  };

  async function uploadLogo() {
    if (!logoFile) return null;
    const fd = new FormData();
    fd.append("tenantId", tenantId);
    fd.append("username", username);
    fd.append("file", logoFile);
    const r = await axios.post(`${API}/api/tenant/mysql/logo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data?.logoPath || null;
  }

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!isEmail(form.email)) {
      Swal.fire(
        "Invalid Email",
        "Please enter a valid email address.",
        "warning"
      );
      return;
    }
    if (!isPhone(form.phone)) {
      Swal.fire(
        "Invalid Phone",
        "Phone must be 50 characters or fewer.",
        "warning"
      );
      return;
    }
    if (!(Number(form.num_users) >= 1)) {
      Swal.fire(
        "Invalid Users",
        "Users must be a positive number.",
        "warning"
      );
      return;
    }
    if (Number(form.num_clients) < 0) {
      Swal.fire(
        "Invalid Client Limit",
        "Client limit cannot be negative.",
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      // 1) Upload logo if chosen
      let uploadedLogo = null;
      if (logoFile) {
        uploadedLogo = await uploadLogo();
        if (uploadedLogo) {
          setForm((f) => ({ ...f, logo_path: uploadedLogo }));
          setLogoPreview(toAbsMediaUrl(uploadedLogo));
        }
      }

      // 2) Patch details
      const payload = {
        company_name: form.company_name || null,
        contact_name: form.contact_name || null,
        email: form.email || null,
        phone: form.phone || null,
        address: form.address || null,
        num_users: Number(form.num_users),
        num_licenses: Number(form.num_licenses),
        num_clients: Number(form.num_clients), // NEW
        user_map: form.user_map || "google",   // NEW
        is_active: form.is_active ? 1 : 0,
        // logo updated by separate endpoint; we keep logo_path read-only here
      };

      await axios.patch(
        `${API}/api/mysql/tenant/${tenantId}/admins/${encodeURIComponent(
          username
        )}`,
        payload
      );

      Swal.fire("Saved", "Admin updated successfully.", "success");
      navigate(`/superAdmin/admin_Menu`);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e?.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const statusPill = form.is_active
    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
    : "bg-rose-50 text-rose-700 ring-1 ring-rose-200";

  return (
    <div className="min-h-screen bg-[radial-gradient(1100px_500px_at_50%_-120px,rgba(15,23,42,0.10),transparent)] bg-slate-100">
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:w-3/4 md:h-full md:overflow-auto">
          <div className="px-3 sm:px-4 lg:px-6 pt-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg shadow-slate-900/30">
                  <UserCog className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-slate-900">
                    Edit Admin —{" "}
                    <span className="text-slate-700">{username}</span>
                  </h1>
                  <p className="text-sm text-slate-600">
                    <span className="inline-flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-500" />
                      Tenant ID: {tenantId}
                    </span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Link
                  to={`/superAdmin/admin_Menu`}
                  className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-1.5 text-slate-800 ring-1 ring-slate-200 hover:bg-slate-50"
                  title="Back to list"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to List
                </Link>
                <div className="rounded-xl bg-white px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-200">
                  <Time />
                </div>
              </div>
            </div>
          </div>

          <div className="px-3 sm:px-4 lg:px-6 py-6">
            <div className="grid grid-cols-12 gap-4 lg:gap-6">
              {/* Main card */}
              <div className="col-span-12 xl:col-span-8 rounded-2xl border border-slate-200/70 bg-white/80 shadow-xl backdrop-blur">
                <div className="flex items-center gap-2 border-b border-slate-200/70 bg-gradient-to-r from-indigo-50 to-blue-50 px-4 py-3 rounded-t-2xl">
                  <ShieldCheck className="h-4 w-4 text-indigo-600" />
                  <h3 className="text-sm font-semibold text-slate-800">
                    Admin Details
                  </h3>
                </div>

                {loading ? (
                  <div className="p-6 text-slate-600">Loading details…</div>
                ) : (
                  <form onSubmit={onSubmit} className="p-4 lg:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Username (read-only) */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Username
                        </label>
                        <input
                          type="text"
                          value={username}
                          disabled
                          className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-slate-900 shadow-sm"
                        />
                      </div>

                      {/* Company Name */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Company Name
                        </label>
                        <div className="relative">
                          <Building2 className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <input
                            name="company_name"
                            type="text"
                            value={form.company_name}
                            onChange={onChange}
                            maxLength={200}
                            placeholder="Company Pvt Ltd"
                            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Contact Name */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Contact Name
                        </label>
                        <input
                          name="contact_name"
                          type="text"
                          value={form.contact_name}
                          onChange={onChange}
                          maxLength={200}
                          placeholder="Contact person"
                          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                        />
                      </div>

                      {/* Email */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Email
                        </label>
                        <div className="relative">
                          <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={onChange}
                            maxLength={256}
                            placeholder="admin@example.com"
                            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Address */}
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Address
                        </label>
                        <div className="relative">
                          <MapPin className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                          <input
                            name="address"
                            type="text"
                            value={form.address}
                            onChange={onChange}
                            maxLength={256}
                            placeholder="Company address"
                            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Phone
                        </label>
                        <div className="relative">
                          <Phone className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <input
                            name="phone"
                            type="text"
                            value={form.phone}
                            onChange={onChange}
                            maxLength={50}
                            placeholder="+91 90000 00000"
                            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                      </div>

                      {/* Users (num_users) */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Users
                        </label>
                        <div className="relative">
                          <UsersIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <input
                            name="num_users"
                            type="number"
                            min={1}
                            value={form.num_users}
                            onChange={onChangeUsers}
                            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          />
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Number of allowed users for this tenant.
                        </p>
                      </div>

                      {/* Visit Client Limit (num_clients) */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Visit Client Limit
                        </label>
                        <div className="relative">
                          <UsersIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <input
                            name="num_clients"
                            type="number"
                            min={0}
                            value={form.num_clients}
                            onChange={onChangeClients}
                            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                            placeholder="e.g. 50"
                          />
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Used mainly for <strong>Patrol Visit</strong> tenants.
                          Stored as <code>num_clients</code>.
                        </p>
                      </div>

                      {/* Map Provider (user_map) */}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Map Provider
                        </label>
                        <div className="relative">
                          <Boxes className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <select
                            name="user_map"
                            value={form.user_map}
                            onChange={onChange}
                            className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
                          >
                            <option value="google">Google Maps</option>
                            <option value="mapbox">Mapbox</option>
                            <option value="none">No Map / Basic</option>
                          </select>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Choose cheaper option (Mapbox / None) if customer
                          can&apos;t afford Google Maps.
                        </p>
                      </div>

                      {/* Logo */}
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Company Logo
                        </label>
                        <div className="flex items-start gap-4">
                          <div className="flex-1">
                            <label className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm hover:bg-slate-50 cursor-pointer">
                              <ImageIcon className="h-4 w-4" />
                              <span>Choose file</span>
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  setLogoFile(f || null);
                                }}
                                className="hidden"
                              />
                            </label>

                            {form.logo_path ? (
                              <p className="mt-1 text-xs text-slate-500">
                                Current:{" "}
                                <code>{toAbsMediaUrl(form.logo_path)}</code>
                              </p>
                            ) : null}

                            <p className="mt-1 text-xs text-slate-500">
                              PNG/JPG/WEBP up to 2MB recommended ~512×512.
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="md:col-span-2">
                        <label className="mb-1 block text-sm font-medium text-slate-700">
                          Status
                        </label>
                        <div className="flex items-center gap-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusPill}`}
                          >
                            {form.is_active ? "Active" : "Inactive"}
                          </span>
                          <button
                            type="button"
                            onClick={onToggleActive}
                            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-white shadow focus:outline-none focus:ring-2 ${
                              form.is_active
                                ? "bg-rose-600 hover:bg-rose-700 focus:ring-rose-500/30"
                                : "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500/30"
                            }`}
                          >
                            {form.is_active ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex items-center justify-end gap-2">
                      <button
                        type="submit"
                        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-white shadow hover:bg-black focus:outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-60"
                        disabled={saving}
                      >
                        <Save className="h-4 w-4" />
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Side tips */}
              <div className="col-span-12 xl:col-span-4 rounded-2xl border border-slate-200/70 bg-white/70 shadow-xl backdrop-blur p-5">
                <h4 className="mb-3 text-sm font-semibold text-slate-800">
                  Tips
                </h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>
                    • Keep usernames stable; use contact/display fields for
                    names.
                  </li>
                  <li>
                    • <b>Users</b> controls allowed logins in this tenant.
                  </li>
                  <li>
                    • <b>Visit Client Limit</b> and <b>Map Provider</b> help you
                    tune Patrol Visit costs for each customer.
                  </li>
                  <li>• Status affects sign-in access for this admin user.</li>
                </ul>

                <div className="mt-5 grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">Tenant</div>
                    <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-slate-500" />
                      {tenantId}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="text-xs text-slate-500">User</div>
                    <div className="text-sm font-medium text-slate-800">
                      {username}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
